/**
 * @file    widgetOptionSyncReducer.ts
 * @scope   SHARED
 * @summary Reducer owning the drawer edits that must be mirrored into the widget draft: the
 *          option patches (goal line, Y axis, text block) and the widget title.
 * @consumes Nothing — pure state transitions.
 * @produces widgetOptionSyncReducer, initialWidgetOptionSyncState, action creators' types
 * @sideEffects None.
 * @notes WHY a reducer: these values used to live as four `useState` in the orchestrator that four
 *        effects copied into the store, so the same data had two owners and each effect published
 *        from its own stale snapshot (the last write silently dropped the others). Accumulating the
 *        edits into ONE pending patch makes the publication atomic and removes the mirroring.
 *        The falsy guards are deliberate and pinned by ../hooks/useWidgetOptionSync.test.ts:
 *        clearing a value does NOT clear it in the widget.
 */

/** Widget option keys written by the drawer's child inputs. */
export const WIDGET_OPTION_KEYS = {
  goal: 'goal',
  yAxis: 'optYAxisValues',
  textBlock: 'textBlock',
} as const;

/** Edits accumulated since the last publication. */
export type WidgetOptionSyncState = {
  /** Partial `customWidget.options` waiting to be merged into the draft. */
  optionsPatch: Record<string, unknown>;
  /** Title waiting to be published, `null` when the title was not edited. */
  displayName: string | null;
  /**
   * Bumped on every accepted edit. The publish effect keys on this instead of on the values, so
   * several edits landing in the same commit produce exactly one publication.
   */
  revision: number;
};

export const initialWidgetOptionSyncState: WidgetOptionSyncState = {
  optionsPatch: {},
  displayName: null,
  revision: 0,
};

/** Transitions accepted by {@link widgetOptionSyncReducer}. */
export type WidgetOptionSyncAction =
  | { type: 'goalChanged'; value: unknown }
  | { type: 'yAxisChanged'; value: unknown }
  | { type: 'textBlockChanged'; value: unknown }
  | { type: 'titleChanged'; value: string }
  | { type: 'published'; revision: number };

/** Queues an option edit, ignoring falsy values (see the file note). */
const queueOption = (
  state: WidgetOptionSyncState,
  key: string,
  value: unknown
): WidgetOptionSyncState => {
  if (!value) return state;
  return {
    ...state,
    optionsPatch: { ...state.optionsPatch, [key]: value },
    revision: state.revision + 1,
  };
};

export const widgetOptionSyncReducer = (
  state: WidgetOptionSyncState,
  action: WidgetOptionSyncAction
): WidgetOptionSyncState => {
  switch (action.type) {
    case 'goalChanged':
      return queueOption(state, WIDGET_OPTION_KEYS.goal, action.value);

    case 'yAxisChanged':
      return queueOption(state, WIDGET_OPTION_KEYS.yAxis, action.value);

    case 'textBlockChanged':
      return queueOption(state, WIDGET_OPTION_KEYS.textBlock, action.value);

    case 'titleChanged':
      if (!action.value) return state;
      return { ...state, displayName: action.value, revision: state.revision + 1 };

    case 'published':
      // WHY the revision guard: an edit may land between the publication and this acknowledgement.
      // Clearing blindly would discard it, so a stale acknowledgement is ignored.
      if (action.revision !== state.revision) return state;
      return { ...initialWidgetOptionSyncState, revision: state.revision };

    default:
      return state;
  }
};
